import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Trees,
  MapPinned,
  BookOpen,
  Users2,
  Heart,
  Compass,
  Search,
  Users,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { AUTH_SIGNUP_URL, CATEGORIES } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const ease = [0.22, 1, 0.36, 1];

const GENEALOGY_ILLUSTRATION = "/portraits/wing_genealogy.png";

// Curated companion plates
const PLATE = {
  document: CATEGORIES.find((c) => c.slug === "heritage")?.image,
  letters: CATEGORIES.find((c) => c.slug === "family-history")?.image,
  portrait: CATEGORIES.find((c) => c.slug === "historical-families")?.image,
  vintage: CATEGORIES.find((c) => c.slug === "genealogy")?.image,
  stories: CATEGORIES.find((c) => c.slug === "family-stories")?.image,
  place: CATEGORIES.find((c) => c.slug === "local-history")?.image,
};

/* ------------------------------------------------------------------ */
/*  Primitives                                                          */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

const Body = ({ children }) => (
  <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
    {children}
  </p>
);
const Prose = ({ children }) => <div className="space-y-6 md:space-y-7">{children}</div>;

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Plate({ src, caption, caption2, aspect = "aspect-[4/5]" }) {
  return (
    <figure className="artifact border border-[color:var(--ink-line-strong)] overflow-hidden">
      <div className={`${aspect} overflow-hidden bg-[color:var(--bone-2)]`}>
        <img src={src} alt="" className="zoom w-full h-full object-cover" loading="lazy" />
      </div>
      <figcaption className="p-4 border-t border-[color:var(--ink-line)] flex items-baseline justify-between gap-4">
        <span className="numeric-index text-[color:var(--ink-soft)]">{caption}</span>
        <span className="numeric-index text-[color:var(--ink-soft)] text-right">{caption2}</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const QUESTIONS = [
  "Who were our ancestors?",
  "Where did they live?",
  "How did they earn a living?",
  "What traditions did they preserve?",
  "Why did they migrate?",
  "What challenges did they overcome?",
  "What stories have been passed down?",
  "How are we connected to one another?",
];

const RELATIVES = [
  "Parents",
  "Grandparents",
  "Great-grandparents",
  "Siblings",
  "Aunts and uncles",
  "Cousins",
  "Extended relatives",
  "Descendants",
];

const DETAILS = [
  "Life stories",
  "Family relationships",
  "Education",
  "Occupations",
  "Military service",
  "Community contributions",
  "Religious traditions",
  "Languages spoken",
  "Migration history",
  "Family businesses",
  "Awards and achievements",
  "Personal memories",
  "Photographs",
  "Voice recordings",
  "Videos",
  "Letters and diaries",
  "Recipes",
  "Family traditions",
];

const JOURNEY = [
  "Villages",
  "Cities",
  "States",
  "Provinces",
  "Countries",
  "Continents",
  "Migration routes",
  "Immigration records",
  "Passenger lists",
  "Land ownership",
  "Census records",
  "Military records",
];

const HISTORY = [
  "Historic wars",
  "Independence movements",
  "Economic transformations",
  "Technological revolutions",
  "Natural disasters",
  "Cultural renaissances",
  "Religious pilgrimages",
  "Community celebrations",
  "Political change",
];

const CULTURE = [
  "Languages",
  "Festivals",
  "Religious traditions",
  "Culinary heritage",
  "Music and dance",
  "Traditional clothing",
  "Family customs",
  "Occupations",
  "Community history",
  "Local folklore",
  "Oral traditions",
];

const RECORDS = [
  "Birth certificates",
  "Marriage certificates",
  "Death records",
  "Family Bibles or prayer books",
  "School records",
  "Military service records",
  "Passports",
  "Immigration documents",
  "Property deeds",
  "Wills",
  "Census records",
  "Newspaper articles",
  "Letters",
  "Diaries",
  "Photographs",
  "Audio recordings",
  "Family videos",
];

const CONTRIBUTIONS = [
  "Family photographs",
  "Stories from elders",
  "Historical documents",
  "Oral histories",
  "Audio interviews",
  "Videos",
  "Recipes",
  "Family traditions",
  "Historical corrections",
  "Missing relatives",
  "Migration stories",
];

const PULSE = [
  "Family Trees",
  "Heritage",
  "Culture",
  "Celebrate a Life memorials",
  "Cemeteries and Mausoleums",
  "Historic Villages",
  "Nature and Landscapes",
  "Weather History",
  "Historical Events",
  "Community Stories",
  "Local History",
  "Maps",
  "Timelines",
  "Historical Newspapers",
  "Photographs",
  "Public Records",
];

const MODERN = [
  "Interactive family trees",
  "Relationship mapping",
  "Geographic migration maps",
  "Historical timelines",
  "Photo galleries",
  "Video collections",
  "Audio memories",
  "Family documents",
  "Memorial pages",
  "Heritage articles",
  "Community discussions",
  "AI-assisted research",
  "Historical record discovery",
];

const WHY_LINES = [
  "It teaches us resilience through the struggles of our ancestors.",
  "It inspires gratitude for the opportunities they created.",
  "It preserves traditions that might otherwise disappear.",
  "It strengthens family connections across generations and continents.",
];

const CLOSING = [
  "Every family has roots.",
  "Every ancestor has a story.",
  "Every generation adds another branch.",
  "Every memory becomes part of a legacy.",
];

const JOURNEY_ACTIONS = [
  "Explore your origins.",
  "Reconnect with relatives.",
  "Preserve your family's history.",
  "Celebrate your heritage.",
  "Leave a lasting legacy for generations to come.",
];

// Five illustration pillars
const PILLARS = [
  { icon: Trees,      label: "Build Your Tree",  desc: "Create your family tree and explore your lineage" },
  { icon: MapPinned,  label: "Discover Places",  desc: "Follow your ancestors' journeys across the world" },
  { icon: BookOpen,   label: "Uncover Stories",  desc: "Preserve stories, memories, photos and documents" },
  { icon: Users2,     label: "Connect Family",   desc: "Invite relatives and build connections across generations" },
  { icon: Heart,      label: "Preserve Legacy",  desc: "Save your heritage today for tomorrow's generations" },
];

// Bottom action strip
const ACTIONS = [
  { icon: Compass,     label: "Explore" },
  { icon: BookOpen,    label: "Record" },
  { icon: Users,       label: "Connect" },
  { icon: ShieldCheck, label: "Preserve" },
  { icon: Sparkles,    label: "Inspire" },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Genealogy() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <main data-testid="genealogy-page" className="pt-32 md:pt-40">
      {/* ---------------- Masthead ---------------- */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Chapter VIII · Genealogy</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">§ Genealogy</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          style={{ y: heroY, fontSize: "clamp(48px, 9vw, 170px)" }}
          className="font-serif-display leading-[0.9] tracking-[-0.03em] max-w-6xl"
        >
          Discovering the stories{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            that connect generations.
          </span>
        </motion.h1>
      </section>

      {/* ---------------- Pull-quote ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(26px, 4.4vw, 64px)" }}
              >
                To know where you are going, it helps to know where you came from.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Opening prose (two paragraphs before the illustration) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">§ Welcome</div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Every family has a story waiting to be discovered.
              </p>
              <Body>
                Some stories are carefully preserved through photographs,
                family trees, handwritten letters, and treasured heirlooms.
                Others are hidden in old records, forgotten villages, faded
                gravestones, or the memories of grandparents. Together, these
                stories form the rich tapestry of our ancestry.
              </Body>
              <Body>
                The Genealogy section of <MFT />, part of the <SP /> platform,
                is dedicated to helping individuals and families explore their
                roots, document their lineage, preserve family history, and
                connect generations through a living digital archive. Genealogy
                is more than tracing names and dates — it is about discovering
                the people, places, traditions, and experiences that shaped
                your family and, ultimately, who you are today.
              </Body>
            </Prose>
          </div>
        </div>
      </section>

      {/* ---------------- Featured illustration ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={GENEALOGY_ILLUSTRATION}
                alt="A warm illustration of three generations gathered around a heritage tree of portraits, an open photo album, laptop and phone showing a family tree, a vintage map of migration routes, a compass, sepia photographs, a notebook of names and relationships, and five pillars marked Build Your Tree, Discover Places, Uncover Stories, Connect Family, Preserve Legacy — captioned Honour the Past, Celebrate the Present, Inspire the Future."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="genealogy-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № G · Every family has a story. What's yours?
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[320px] hidden md:block">
              Honour the past · Celebrate the present · Inspire the future
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* ---------------- Chapter 01 · What Is Genealogy? ---------------- */}
      <Chapter n="01" title="What is genealogy?" subtitle="Timeless questions.">
        <Prose>
          <Body>
            Genealogy is the study and documentation of family relationships
            across generations. It seeks to answer timeless questions:
          </Body>
          <Ledger items={QUESTIONS} columns={2} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Every answer reveals another piece of your family's history and
            strengthens your connection to the past.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 02 · Every Family Has a Story ---------------- */}
      <Chapter n="02" title="Every family" subtitle="has a story.">
        <Prose>
          <Body>
            Not every family appears in history books, yet every family has
            contributed to the story of humanity.
          </Body>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-4">
                {[
                  "The farmer who cultivated the land.",
                  "The artisan who perfected a traditional craft.",
                  "The teacher who inspired generations.",
                  "The merchant who built a local business.",
                  "The nurse who cared for a community.",
                  "The soldier who served during times of conflict.",
                  "The parent who worked tirelessly to provide a better future.",
                ].map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(18px, 1.7vw, 22px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>
                These lives deserve to be remembered, celebrated, and preserved.
              </Body>
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Genealogy gives voice to those whose stories might otherwise be
                forgotten.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.portrait}
                caption="Plate № 001"
                caption2="Undated · Unnamed · Unforgotten"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 03 · Building Your Family Tree ---------------- */}
      <Chapter n="03" title="Building your" subtitle="family tree.">
        <Prose>
          <Body>Your family tree is the foundation of your genealogy journey.</Body>
          <Body>Begin with yourself and gradually connect:</Body>
          <Ledger items={RELATIVES} columns={2} />
          <Body>
            As your tree grows, so does your understanding of your family's
            journey across time. <MFT /> makes it easy to create living family
            trees enriched with photographs, biographies, timelines, documents,
            and memories.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 04 · More Than Names and Dates ---------------- */}
      <Chapter n="04" title="More than" subtitle="names and dates.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                A complete genealogy records much more than births and deaths.
              </Body>
              <Body>Preserve details such as:</Body>
              <Ledger items={DETAILS} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Every detail adds depth to your family's story.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.document}
                caption="Plate № 002"
                caption2="A register, kept in the family Bible"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 05 · Following the Journey of Your Ancestors ---------------- */}
      <Chapter n="05" title="Following the journey" subtitle="of your ancestors.">
        <Prose>
          <Body>
            Families have always moved in search of opportunity, safety,
            education, trade, and new beginnings.
          </Body>
          <Body>Genealogy helps trace these journeys through:</Body>
          <Ledger items={JOURNEY} columns={3} />
          <Body>
            Understanding where your family lived and why they moved provides
            valuable insight into your heritage.
          </Body>
          <div className="mt-4">
            <Link
              to="/cemeteries"
              className="btn-ghost text-[13px]"
              data-testid="gen-cemeteries-cta"
            >
              Browse the cemeteries directory <span aria-hidden>→</span>
            </Link>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 06 · Connecting Family and History ---------------- */}
      <Chapter n="06" title="Connecting family" subtitle="and history.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.letters}
                caption="Plate № 003"
                caption2="Letters home · A century apart"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>Every family lives through history.</Body>
              <Body>Your ancestors may have witnessed:</Body>
              <Ledger items={HISTORY} columns={2} />
              <Body>
                Genealogy places your family within the larger story of
                humanity, showing how personal lives intersect with world
                events.
              </Body>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 07 · Discovering Your Cultural Heritage ---------------- */}
      <Chapter n="07" title="Discovering your" subtitle="cultural heritage.">
        <Prose>
          <Body>Genealogy is closely connected to culture.</Body>
          <Body>Document your family's:</Body>
          <Ledger items={CULTURE} columns={3} />
          <Body>
            Understanding these traditions helps preserve your family's unique
            identity for future generations.
          </Body>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/category/heritage"
              className="btn-ghost text-[13px]"
              data-testid="gen-heritage-cta"
            >
              Read the Heritage article
            </Link>
            <Link
              to="/culture"
              className="btn-ghost text-[13px]"
              data-testid="gen-culture-cta"
            >
              Explore Culture
            </Link>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 08 · Preserving Family Records ---------------- */}
      <Chapter n="08" title="Preserving" subtitle="family records.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                Some of the most valuable genealogical resources are already
                within your family. Consider preserving:
              </Body>
              <Ledger items={RECORDS} columns={2} />
              <Body>
                Digitising these records ensures they remain accessible for
                generations to come.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.vintage}
                caption="Plate № 004"
                caption2="A portrait, uncatalogued"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 09 · Genealogy Around the World ---------------- */}
      <Chapter n="09" title="Genealogy" subtitle="around the world.">
        <Prose>
          <Body>Every culture preserves family history in different ways.</Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-4">
            {[
              "Some maintain detailed written family records.",
              "Others preserve ancestry through oral traditions, clan histories, temple records, community registries, church books, mosque records, synagogue archives, or local storytellers.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            <MFT /> welcomes every family, every tradition, and every heritage.
            Whether your ancestors came from a rural village, a bustling city,
            or crossed oceans to build new lives, your story is part of the
            world's shared human history.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 10 · Collaborative Journey ---------------- */}
      <Chapter n="10" title="A collaborative" subtitle="journey.">
        <Prose>
          <Body>Family history becomes richer when it is shared.</Body>
          <Body>Invite relatives to contribute:</Body>
          <Ledger items={CONTRIBUTIONS} columns={2} />
          <Body>
            Together, families can build a living archive that becomes more
            complete with every generation.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 11 · Powered by SocialPulse ---------------- */}
      <Chapter n="11" title="Powered by" subtitle="SocialPulse.">
        <Prose>
          <Body>
            Genealogy on <MFT /> is connected to the broader <SP /> ecosystem,
            allowing you to explore:
          </Body>
          <Ledger items={PULSE} columns={2} />
          <Body>
            By connecting people with places, events, and communities, <SP />{" "}
            creates a deeper understanding of both family history and world
            history.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 12 · Modern Genealogy ---------------- */}
      <Chapter n="12" title="Modern genealogy for" subtitle="a digital world.">
        <Prose>
          <Body>Genealogy has evolved beyond paper charts and filing cabinets.</Body>
          <Body>
            With <MFT />, your family's history becomes a dynamic, searchable,
            multimedia archive that can include:
          </Body>
          <Ledger items={MODERN} columns={2} />
          <p
            className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3] mt-6"
            style={{ fontSize: "clamp(20px, 2.2vw, 30px)" }}
          >
            Your family's story is no longer confined to a single book — it
            becomes a living legacy that grows with every contribution.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 13 · Why Genealogy Matters ---------------- */}
      <Chapter n="13" title="Why genealogy" subtitle="matters.">
        <Prose>
          <Body>
            Genealogy reminds us that we are part of something larger than
            ourselves.
          </Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {WHY_LINES.map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            Most importantly, it ensures that future generations will know not
            only the names of those who came before them, but the lives they
            lived, the dreams they pursued, and the values they passed on.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Five pillars strip (from illustration) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink-line)]">
        <div className="overline text-[color:var(--ink-soft)] mb-8">
          § The five threads of genealogy
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease }}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-7 min-h-[220px] flex flex-col justify-between bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`gen-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-full border border-[color:var(--ink)] flex items-center justify-center text-[color:var(--rust)]">
                  <Icon size={20} />
                </div>
                <div>
                  <div
                    className="font-serif-display leading-[1] mt-6"
                    style={{ fontSize: "clamp(19px, 1.8vw, 26px)" }}
                  >
                    {p.label}
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[color:var(--ink-soft)]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Closing manifesto ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § Begin your journey
        </div>

        <div className="space-y-3 md:space-y-4 mb-12">
          {CLOSING.map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease }}
              className="font-serif-display leading-[1.05]"
              style={{ fontSize: "clamp(26px, 4vw, 60px)" }}
            >
              {line}
            </motion.p>
          ))}
        </div>

        <Body>
          Start your genealogy journey today with <MFT />.
        </Body>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)] mt-8">
          {JOURNEY_ACTIONS.map((a, i) => (
            <div
              key={i}
              className="border-r border-b border-[color:var(--ink-line)] p-5 md:p-6 min-h-[160px] flex flex-col justify-between"
            >
              <div className="numeric-index text-[color:var(--ink-soft)]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(17px, 1.6vw, 22px)" }}
              >
                {a}
              </p>
            </div>
          ))}
        </div>

        <p
          className="mt-10 font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
          style={{ fontSize: "clamp(22px, 3vw, 40px)" }}
        >
          Because every family deserves to know its story.
        </p>

        {/* Action strip mirroring the illustration bottom row */}
        <div className="mt-14 grid grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {ACTIONS.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={a.label}
                className="border-r border-b border-[color:var(--ink-line)] p-4 md:p-6 flex flex-col items-center justify-center gap-2 text-center"
              >
                <Icon size={20} className="text-[color:var(--rust)]" />
                <div className="numeric-index text-[color:var(--ink)]">{a.label}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <a
            href={AUTH_SIGNUP_URL}
            className="btn-rust"
            data-testid="gen-cta-primary"
          >
            Begin your genealogy archive <span aria-hidden>→</span>
          </a>
          <Link to="/category/family-tree" className="btn-ghost" data-testid="gen-cta-tree">
            Build a family tree
          </Link>
          <Link to="/cemeteries" className="btn-ghost" data-testid="gen-cta-cemeteries">
            Search cemeteries
          </Link>
        </div>
      </section>

      {/* ---------------- Signature ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
              >
                Genealogy
                <span className="text-[color:var(--rust)]"> · </span>
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  MyFamilyTree
                </span>
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(16px, 1.8vw, 22px)" }}
              >
                Discover your roots · Connect the generations · Preserve your legacy.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <p
                className="font-italic-serif italic text-[color:var(--ink)] leading-[1.35]"
                style={{ fontSize: "clamp(16px, 1.6vw, 20px)" }}
              >
                Every name tells a story. Every branch reveals a journey.
                Every family is a living chapter in the history of humanity.
              </p>
              <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">
                Yesterday's roots · Today's connections · Tomorrow's legacy.
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
